import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResponseDialog({ open, onOpenChange, customer, onDone }) {
  const { meta } = useMeta();
  const [status, setStatus] = useState("interested");
  const [exactText, setExactText] = useState("");
  const [summary, setSummary] = useState("");
  const [orderValue, setOrderValue] = useState("");
  const [changeStage, setChangeStage] = useState(false);
  const [newStage, setNewStage] = useState(customer.stage);
  const [newSub, setNewSub] = useState("");
  const [loading, setLoading] = useState(false);

  const stageObj = (meta?.stages || []).find((s) => s.key === newStage);
  const showOrderValue = status === "order_confirmed" || status === "order_expected";

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/customers/${customer.id}/responses`, {
        status, exact_text: exactText || null, summary: summary || null,
        order_value: showOrderValue && orderValue ? Number(orderValue) : null,
        change_stage: changeStage, new_stage: changeStage ? newStage : null,
        new_sub_stage: changeStage ? (newSub || null) : null,
      });
      toast.success("Customer response recorded");
      onDone?.(r.data.customer);
      onOpenChange(false);
      setExactText(""); setSummary(""); setOrderValue(""); setChangeStage(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Record customer response</DialogTitle>
          <DialogDescription>Log exactly what the customer said. This resets the unanswered follow-up count.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Response status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="response-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {(meta?.response_statuses || []).map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Customer's exact message (optional)</Label>
            <Textarea value={exactText} onChange={(e) => setExactText(e.target.value)} rows={2} placeholder="Paste what the customer wrote" data-testid="response-exact-text" />
          </div>
          <div className="space-y-1.5">
            <Label>Short summary (optional)</Label>
            <Input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="e.g. Asked for rate on BP grade" data-testid="response-summary" />
          </div>
          {showOrderValue && (
            <div className="space-y-1.5">
              <Label>Order value (₹){status === "order_confirmed" ? "" : " — expected"}</Label>
              <Input type="number" min="0" step="1" value={orderValue} onChange={(e) => setOrderValue(e.target.value)} placeholder="e.g. 45000" data-testid="response-order-value" />
              <p className="text-[11px] text-stone-400">Used for revenue goals and the leaderboard.</p>
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
            <span className="text-sm text-stone-600">Change the sales stage</span>
            <Switch checked={changeStage} onCheckedChange={setChangeStage} data-testid="response-change-stage" />
          </div>
          {changeStage && (
            <div className="grid gap-2">
              <Select value={newStage} onValueChange={setNewStage}>
                <SelectTrigger data-testid="response-new-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.stages || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={newSub} onValueChange={setNewSub}>
                <SelectTrigger data-testid="response-new-substage"><SelectValue placeholder="Sub-stage (optional)" /></SelectTrigger>
                <SelectContent>
                  {(stageObj?.sub_stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={submit} disabled={loading} data-testid="response-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record response"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
